import type { ModelDefinition } from "@/models.js";

const xaiSupportedParamsNoFreqPresence = [
	"temperature",
	"max_tokens",
	"top_p",
	"response_format",
	"tools",
	"tool_choice",
];

export const xaiModels = [
	{
		id: "grok-3",
		name: "Grok-3",
		description: "xAI's advanced model with strong reasoning capabilities.",
		family: "xai",
		releasedAt: new Date("2025-02-17"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-3",
				inputPrice: "3.0e-6",
				cachedInputPrice: "0.75e-6",
				outputPrice: "15.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deactivatedAt: new Date("2026-05-15"),
			},
		],
	},
	{
		id: "grok-3-mini",
		name: "Grok-3 Mini",
		description: "Compact Grok-3 for fast, cost-effective inference.",
		family: "xai",
		releasedAt: new Date("2025-04-09"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-3-mini",
				inputPrice: "0.3e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deprecatedAt: new Date("2026-01-30"),
				deactivatedAt: new Date("2026-02-28"),
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-3-fast",
		name: "Grok-3 Fast",
		description: "Optimized Grok-3 variant for lower latency.",
		family: "xai",
		releasedAt: new Date("2025-05-22"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-3-fast",
				inputPrice: "5.0e-6",
				outputPrice: "25.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deprecatedAt: new Date("2025-08-08"),
				deactivatedAt: new Date("2025-09-15"),
			},
		],
	},
	{
		id: "grok-3-mini-fast",
		name: "Grok-3 Mini Fast",
		description: "Fastest Grok-3 Mini variant.",
		family: "xai",
		releasedAt: new Date("2025-02-17"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-3-mini-fast",
				inputPrice: "0.6e-6",
				outputPrice: "4.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deprecatedAt: new Date("2025-08-08"),
				deactivatedAt: new Date("2025-09-15"),
			},
		],
	},
	{
		id: "grok-2-1212",
		name: "Grok-2 (1212)",
		description: "December 2024 version of Grok-2.",
		family: "xai",
		releasedAt: new Date("2024-12-12"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-2-1212",
				inputPrice: "2.0e-6",
				outputPrice: "10.0e-6",
				requestPrice: "0",
				contextSize: 131072,
				maxOutput: undefined,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				deprecatedAt: new Date("2025-08-08"),
				deactivatedAt: new Date("2025-09-15"),
			},
		],
	},
	{
		id: "grok-2-vision-1212",
		name: "Grok-2 Vision (1212)",
		description: "Grok-2 with vision capabilities.",
		family: "xai",
		releasedAt: new Date("2024-12-14"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-2-vision-1212",
				inputPrice: "2.0e-6",
				outputPrice: "10.0e-6",
				requestPrice: "0",
				imageInputPrice: "2.0e-6",
				contextSize: 32768,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				deprecatedAt: new Date("2025-08-08"),
				deactivatedAt: new Date("2025-09-15"),
			},
		],
	},
	{
		id: "grok-4-0709",
		name: "Grok 4 (0709)",
		description: "July 2025 version of Grok 4.",
		family: "xai",
		releasedAt: new Date("2025-07-09"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4-0709",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "3.0e-6",
				outputPrice: "15.0e-6",
				pricingTiers: [
					{
						name: "Up to 128K",
						upToTokens: 128000,
						inputPrice: "3.0e-6",
						outputPrice: "15.0e-6",
						cachedInputPrice: "0.75e-6",
					},
					{
						name: "Over 128K",
						upToTokens: Infinity,
						inputPrice: "6.0e-6",
						outputPrice: "30.0e-6",
						cachedInputPrice: "0",
					},
				],
				cachedInputPrice: "0.75e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 256000,
				maxOutput: 256000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4",
		name: "Grok 4",
		description: "Latest Grok 4 with vision and tool support.",
		family: "xai",
		releasedAt: new Date("2025-07-09"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4",
				inputPrice: "3.0e-6",
				outputPrice: "15.0e-6",
				pricingTiers: [
					{
						name: "Up to 128K",
						upToTokens: 128000,
						inputPrice: "3.0e-6",
						outputPrice: "15.0e-6",
						cachedInputPrice: "0.75e-6",
					},
					{
						name: "Over 128K",
						upToTokens: Infinity,
						inputPrice: "6.0e-6",
						outputPrice: "30.0e-6",
						cachedInputPrice: "0",
					},
				],
				cachedInputPrice: "0.75e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 256000,
				maxOutput: 256000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-fast-reasoning",
		name: "Grok 4 Fast Reasoning",
		description: "Grok 4 optimized for fast reasoning.",
		family: "xai",
		releasedAt: new Date("2025-07-09"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4-fast-reasoning",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "0.2e-6",
				outputPrice: "0.5e-6",
				pricingTiers: [
					{
						name: "Up to 128K",
						upToTokens: 128000,
						inputPrice: "0.2e-6",
						outputPrice: "0.5e-6",
						cachedInputPrice: "0.05e-6",
					},
					{
						name: "Over 128K",
						upToTokens: Infinity,
						inputPrice: "0.4e-6",
						outputPrice: "1.0e-6",
						cachedInputPrice: "0",
					},
				],
				cachedInputPrice: "0.05e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				reasoning: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-fast-non-reasoning",
		name: "Grok 4 Fast Non-Reasoning",
		description: "Grok 4 without reasoning for faster responses.",
		family: "xai",
		releasedAt: new Date("2025-10-10"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4-fast-non-reasoning",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "0.2e-6",
				outputPrice: "0.5e-6",
				pricingTiers: [
					{
						name: "Up to 128K",
						upToTokens: 128000,
						inputPrice: "0.2e-6",
						outputPrice: "0.5e-6",
						cachedInputPrice: "0.05e-6",
					},
					{
						name: "Over 128K",
						upToTokens: Infinity,
						inputPrice: "0.4e-6",
						outputPrice: "1.0e-6",
						cachedInputPrice: "0",
					},
				],
				cachedInputPrice: "0.05e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-code-fast-1",
		name: "Grok Code Fast 1",
		description: "Grok optimized for code generation tasks.",
		family: "xai",
		releasedAt: new Date("2025-08-28"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-code-fast-1",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "0.2e-6",
				cachedInputPrice: "0.02e-6",
				outputPrice: "1.5e-6",
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: 10000,
				streaming: true,
				vision: false,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-1-fast-reasoning",
		name: "Grok 4.1 Fast Reasoning",
		description: "Grok 4.1 with fast reasoning and vision support.",
		family: "xai",
		releasedAt: new Date("2025-11-19"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4-1-fast-reasoning",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "0.2e-6",
				outputPrice: "0.5e-6",
				pricingTiers: [
					{
						name: "Up to 128K",
						upToTokens: 128000,
						inputPrice: "0.2e-6",
						outputPrice: "0.5e-6",
						cachedInputPrice: "0.05e-6",
					},
					{
						name: "Over 128K",
						upToTokens: Infinity,
						inputPrice: "0.4e-6",
						outputPrice: "1.0e-6",
						cachedInputPrice: "0",
					},
				],
				cachedInputPrice: "0.05e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				reasoning: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
			{
				providerId: "azure-ai-foundry",
				externalId: "grok-4-1-fast-reasoning",
				inputPrice: "0.2e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				reasoning: true,
				reasoningOutput: "omit",
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-1-fast-non-reasoning",
		name: "Grok 4.1 Fast Non-Reasoning",
		description: "Grok 4.1 without reasoning mode.",
		family: "xai",
		releasedAt: new Date("2025-11-19"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4-1-fast-non-reasoning",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "0.2e-6",
				outputPrice: "0.5e-6",
				pricingTiers: [
					{
						name: "Up to 128K",
						upToTokens: 128000,
						inputPrice: "0.2e-6",
						outputPrice: "0.5e-6",
						cachedInputPrice: "0.05e-6",
					},
					{
						name: "Over 128K",
						upToTokens: Infinity,
						inputPrice: "0.4e-6",
						outputPrice: "1.0e-6",
						cachedInputPrice: "0",
					},
				],
				cachedInputPrice: "0.05e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
			{
				providerId: "azure-ai-foundry",
				externalId: "grok-4-1-fast-non-reasoning",
				inputPrice: "0.2e-6",
				outputPrice: "0.5e-6",
				requestPrice: "0",
				imageInputPrice: undefined,
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-20-multi-agent-beta-0309",
		name: "Grok 4.20 Multi-Agent Beta (0309)",
		description:
			"Grok 4.20 multi-agent model with reasoning, vision, and tool support.",
		family: "xai",
		releasedAt: new Date("2026-03-09"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4.20-multi-agent-beta-0309",
				// xAI multi-agent models do not work with the Chat Completions API.
				// They require the Responses API (/v1/responses) with orchestrated sub-agents.
				// Deactivated until the gateway adds xAI Responses API routing support.
				// Ref: https://docs.x.ai/developers/model-capabilities/text/multi-agent
				deactivatedAt: new Date("2026-03-27"),
				inputPrice: "2.0e-6",
				outputPrice: "6.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2.0e-6",
						outputPrice: "6.0e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				reasoning: true,
				reasoningOutput: "omit",
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-20-beta-0309-reasoning",
		name: "Grok 4.20 Beta Reasoning (0309)",
		description: "Grok 4.20 reasoning model with vision and tool support.",
		family: "xai",
		releasedAt: new Date("2026-03-09"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4.20-beta-0309-reasoning",
				inputPrice: "2.0e-6",
				outputPrice: "6.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2.0e-6",
						outputPrice: "6.0e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				reasoning: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-20-beta-0309-non-reasoning",
		name: "Grok 4.20 Beta Non-Reasoning (0309)",
		description: "Grok 4.20 non-reasoning model with vision and tool support.",
		family: "xai",
		releasedAt: new Date("2026-03-09"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4.20-beta-0309-non-reasoning",
				inputPrice: "2.0e-6",
				outputPrice: "6.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2.0e-6",
						outputPrice: "6.0e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-20-reasoning",
		name: "Grok 4.20 Reasoning",
		description: "Grok 4.20 reasoning model with vision and tool support.",
		family: "xai",
		releasedAt: new Date("2026-03-09"),
		providers: [
			{
				providerId: "vertex-openai",
				stability: "unstable" as const,
				externalId: "grok-4.20-reasoning",
				inputPrice: "2.0e-6",
				outputPrice: "6.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2.0e-6",
						outputPrice: "6.0e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				reasoning: true,
				reasoningOutput: "omit",
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-20-non-reasoning",
		name: "Grok 4.20 Non-Reasoning",
		description: "Grok 4.20 non-reasoning model with vision and tool support.",
		family: "xai",
		releasedAt: new Date("2026-03-09"),
		providers: [
			{
				providerId: "vertex-openai",
				stability: "unstable" as const,
				externalId: "grok-4.20-non-reasoning",
				inputPrice: "2.0e-6",
				outputPrice: "6.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "2.0e-6",
						outputPrice: "6.0e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "4.0e-6",
						outputPrice: "12.0e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 2_000_000,
				maxOutput: 30000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-4-3",
		name: "Grok 4.3",
		description:
			"xAI's flagship reasoning model with 1M context, vision, and tool support.",
		family: "xai",
		releasedAt: new Date("2026-04-30"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-4.3",
				inputPrice: "1.25e-6",
				outputPrice: "2.5e-6",
				cachedInputPrice: "0.3125e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "1.25e-6",
						outputPrice: "2.5e-6",
						cachedInputPrice: "0.3125e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "2.5e-6",
						outputPrice: "5.0e-6",
						cachedInputPrice: "0",
					},
				],
				requestPrice: "0",
				contextSize: 1_000_000,
				maxOutput: undefined,
				streaming: true,
				vision: true,
				reasoning: true,
				tools: true,
				jsonOutput: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
	{
		id: "grok-imagine-image-pro",
		name: "Grok Imagine Image Pro",
		description:
			"xAI's premium image generation model with image-to-image support.",
		family: "xai",
		output: ["image"],
		releasedAt: new Date("2026-03-02"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-imagine-image-pro",
				deactivatedAt: new Date("2026-05-15"),
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.07",
				imageInputPrice: "0.002",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "grok-imagine-image",
		name: "Grok Imagine Image",
		description:
			"xAI's fast image generation model with image-to-image support.",
		family: "xai",
		output: ["image"],
		releasedAt: new Date("2026-03-02"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-imagine-image",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0.02",
				imageInputPrice: "0.002",
				contextSize: 2000,
				maxOutput: 4096,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				imageGenerations: true,
			},
		],
	},
	{
		id: "grok-imagine-video-1-5-preview",
		name: "Grok Imagine Video 1.5 Preview",
		description:
			"xAI's image-to-video generation model. Requires an input image. Creates videos up to 15 seconds at 480p or 720p.",
		imageInputRequired: true,
		family: "xai",
		output: ["video"],
		releasedAt: new Date("2026-05-30"),
		providers: [
			{
				test: "skip",
				providerId: "xai",
				externalId: "grok-imagine-video-1.5-preview",
				inputPrice: "0",
				outputPrice: "0",
				requestPrice: "0",
				imageInputPrice: "0.01",
				perSecondPrice: {
					"480p": "0.08",
					"720p": "0.14",
					default: "0.14",
				},
				contextSize: 2000,
				maxOutput: 1,
				streaming: false,
				vision: true,
				tools: false,
				jsonOutput: false,
				videoGenerations: true,
				supportedVideoSizes: ["848x480", "1280x720", "1696x960", "1920x1080"],
				supportedVideoDurationsSeconds: [6, 8, 10, 12, 15],
				supportsVideoAudio: true,
				supportsVideoWithoutAudio: false,
			},
		],
	},
	{
		id: "grok-build-0-1",
		name: "Grok Build 0.1",
		description:
			"xAI's fast coding model for agentic software engineering workflows.",
		family: "xai",
		releasedAt: new Date("2026-05-20"),
		providers: [
			{
				providerId: "xai",
				contentFilterPrice: 0.05,
				externalId: "grok-build-0.1",
				inputPrice: "1.0e-6",
				outputPrice: "2.0e-6",
				pricingTiers: [
					{
						name: "Up to 200K",
						upToTokens: 200000,
						inputPrice: "1.0e-6",
						outputPrice: "2.0e-6",
						cachedInputPrice: "0.2e-6",
					},
					{
						name: "Over 200K",
						upToTokens: Infinity,
						inputPrice: "2.0e-6",
						outputPrice: "4.0e-6",
						cachedInputPrice: "0.4e-6",
					},
				],
				cachedInputPrice: "0.2e-6",
				requestPrice: "0",
				contextSize: 256000,
				maxOutput: 256000,
				streaming: true,
				vision: true,
				tools: true,
				jsonOutput: true,
				reasoning: true,
				supportedParameters: xaiSupportedParamsNoFreqPresence,
			},
		],
	},
] as const satisfies ModelDefinition[];
